"use client";
import React, { useState, useRef, useEffect } from "react";
import { LineChart, Line, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import moment from "moment";
import useUnlistedGraphData from "@/app/alts/unlisted-shares/components/filtere-graph-data";
import { ISINPrices, TotalPaymentResponse } from "@/app/interface/assetpage";
import { themes } from "@/app/enums/chart-themes";
import { Info } from "lucide-react";
import { formatIndianCurrency } from "@/app/utils/helper";
import TopPicks from "@/app/assets/icons/top-picks";
import { Badge, BadgeScheme, BadgeVariant } from "@/stories/badge/badge";
// import TopPicks from "@/app/assets/icons/top-picks";

type PeriodType = "1_month" | "3_months" | "1_year" | "2_years" | "3_years" | "all";

interface PeriodMapping {
    key: PeriodType;
    label: string;
}

interface AssetDetails {
    ISIN: string;
    productTagline?: string;
    company: {
        logoUrl: string;
        displayName: string;
    };
}

interface ChartDataPoint {
    stroke: string;
    strokeWidth: number;
    dataKey: string;
    name: string;
    color: string;
    value: number;
    payload: {
        x: number;
        y: number;
    };
    hide: boolean;
}

interface Tooltips {
    active?: boolean;
    payload?: ChartDataPoint[];
    label?: string;
}

interface Cursor {
    x: number;
    y: number;
}
interface CursorParent {
    points: Cursor[];
}
interface VizProps {
    assetDetails: AssetDetails;
    totalPayment: TotalPaymentResponse | null;
    priceDiff: number | null;
    perDiff: number | null;
    timePeriod: string;
    setTimePeriod: (value: PeriodType) => void;
    unitPrice?: ISINPrices;
    isMobileWidgetOpen: boolean;
}

const periodMappings: PeriodMapping[] = [
    { key: "1_month", label: "1 M" },
    { key: "3_months", label: "3 M" },
    { key: "1_year", label: "1 Y" },
    { key: "2_years", label: "2 Y" },
    { key: "3_years", label: "3 Y" },
    { key: "all", label: "All" },
];

const CustomTooltip = ({ active, payload, label }: Tooltips) => {
    if (active && payload && payload.length) {
        const formattedDate = typeof label === "number" 
            ? moment(label).format("DD/MM/YYYY")
            : moment(label, "MM/DD/YYYY").format("DD/MM/YYYY");
        return (
            <div className="rounded bg-white p-2 text-sm shadow-md">
                <div className="text-gray-600">
                    ₹{payload[0].value?.toFixed(2)} | {formattedDate}
                </div>
            </div>
        );
    }
    return null;
};

const CustomCursor = ({ points }: CursorParent) => {
    return (
        <line
            x1={points[0].x}
            y1={0}
            x2={points[0].x}
            y2="100%"
            stroke="#6B7280"
            strokeWidth="1"
            strokeDasharray="3 3"
        />
    );
};

const Viz: React.FC<VizProps> = ({
    assetDetails,
    totalPayment,
    priceDiff,
    perDiff,
    timePeriod,
    setTimePeriod,
    unitPrice,
    isMobileWidgetOpen,
}) => {
    const [period, setPeriod] = useState<PeriodType>("1_year");
    const { filteredData, error, refetch } = useUnlistedGraphData({
        isin: assetDetails?.ISIN,
        period,
        refetch: false,
    });

    const [data, setData] = useState<{ x: number; y: number }[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (filteredData && filteredData.length > 0) {
            setData(filteredData);
        }
    }, [filteredData]);

    const formatXAxis = (timestamp: number) => {
        return moment(timestamp).format("DD/MM/YY");
    };

    const handlePeriodChange = (newPeriod: PeriodType) => {
        setTimePeriod(newPeriod);
        setPeriod(newPeriod);
        refetch();
    };

    const getLabelForKey = (key: string): string | null => {
        const found = periodMappings.find((item) => item.key === key);
        return found ? found.label : null;
    };

    if (error) return <div>Error loading data</div>;

    return (
        <div className="rounded-3xl bg-white sm:w-[830px] sm:border sm:border-gray-200 sm:p-6">
            <div className="flex justify-between">
                <div className="align-items-center mb-8 flex sm:mb-4">
                    <div className="flex items-center">
                        <span className="pl-0 text-4xl font-semibold sm:pl-4">
                            {formatIndianCurrency(unitPrice?.unitPrice ?? 0)}
                        </span>
                    </div>
                    <div className="flex items-center">
                        <span className="font-weight-bold">
                            {totalPayment?.data?.unitPrice?.toFixed(2)}
                        </span>
                        <span
                            className={`detail padding-x-20 font-size-14 my-auto ml-3 pb-0 ${priceDiff && priceDiff < 0 ? "text-brand-primary-500" : "text-emerald-700"} `}
                        >
                            {priceDiff !== null
                                ? `${priceDiff > 0 ? "+" + priceDiff : priceDiff} (${perDiff}%)`
                                : "--"}
                            <span className="text-gray-500">{` ${getLabelForKey(timePeriod)}`}</span>
                        </span>
                    </div>
                    <div className="relative top-4 z-10 ml-2">
                        <button className="group relative">
                            <Info
                                width="16"
                                height="16"
                                className={`${isMobileWidgetOpen ? "text-transparent" : "text-neutral-300"} cursor-pointer`}
                            />
                            <div className="absolute left-1/2 top-full mt-2 hidden w-56 md:w-80 -translate-x-[80%] rounded-md bg-black p-4 text-xs text-white group-hover:block sm:left-1/2 sm:mx-auto sm:-translate-x-1/2 md:left-1/2 md:-translate-x-1/2">
                                The historical prices shown on the InCred Money platform have been
                                arrived at by using a combination of internal and external sources
                                which reflect the demand and supply situation that is further based
                                on factors like Financial performance, IPO related news, etc. These
                                prices are indicative in nature. We do not recommend investment
                                decisions taken solely based on the historical prices mentioned on
                                our platform.
                            </div>
                        </button>
                    </div>
                </div>
                <div className="hidden sm:block">
                    {assetDetails?.productTagline && (
                        <Badge
                            label={assetDetails?.productTagline}
                            showIcon={false}
                            icon={<TopPicks />}
                            variant={BadgeVariant.Success}
                            scheme={BadgeScheme.Light}
                            showCross={false}
                        />
                    )}
                </div>
            </div>

            <section ref={containerRef} className="viz-section-width">
                <div className="h-[320px] w-full">
                    <ResponsiveContainer>
                        <LineChart data={data} margin={{ top: 20, right: 15, bottom: 5, left: 15 }}>
                            <XAxis dataKey="x" tickFormatter={formatXAxis} hide={true} />
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={<CustomCursor points={[]} />}
                                position={{ y: 0 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="y"
                                stroke={themes.incred.primary}
                                strokeWidth={1.5}
                                dot={false}
                                activeDot={{ r: 8 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>
            <div className="flex justify-center pb-4 pt-6 sm:py-3">
                {periodMappings.map(({ key, label }) => (
                    <button
                        key={key}
                        className={`mx-sm-3 rounded-round py-[6px] sm:mx-1 ${period === key ? "active bg-orange-100" : ""} px-3 sm:px-4`}
                        onClick={() => handlePeriodChange(key as PeriodType)}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default Viz;
